
import {
  computed, ComputedRef, defineComponent, onMounted, PropType, ref, Ref,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { ListSettings } from '@/composables/GraphQL';
import { SpmTableFilter, StringMap } from '@/types';
import { showToastError } from '@/helpers';
import { OperatorType } from '@/types/generated-types/graphql';

const PAGE_SIZE = 50;

// Types supportant FULLTEXT search avec recherche par ID
const FULLTEXT_CONFIG: Record<string, string> = {
  ShopsProducts: 'id_product',
  ShopsProductsCategoriesList: 'id_category',
  ShopsManufacturers: 'id_manufacturer',
};

export default defineComponent({
  name: 'ChooseItemsModal',

  components: {
    Dialog,
    InputText,
    Button,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },

    header: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    fields: {
      type: Object as PropType<StringMap>,
      required: true,
    },

    items: {
      type: String,
      required: false,
      default: '',
    },

    defaultFilters: {
      type: Array as PropType<SpmTableFilter[]>,
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-choose-items': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);
    const searchValue: Ref<string> = ref('');
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const settings: Ref<ListSettings> = ref({
      limit: PAGE_SIZE,
      offset: 0,
      order: [],
      filter: [],
    });

    const availableList: Ref<StringMap[]> = ref([]);
    const selectedList: Ref<StringMap[]> = ref([]);

    // AbortController pour annuler les requêtes en cours
    let currentAbortController: AbortController | null = null;

    const fetchDataWithSignal = async (fetchSettings: ListSettings, signal: AbortSignal): Promise<StringMap[]> => {
      const fname = `${props.name}List`;
      // Limiter aux champs essentiels pour optimiser la requête
      const fieldsStr = `${props.fields.id} ${props.fields.elementId} ${props.fields.label}`;

      const query = `query ($limit: Int!, $offset: Int!, $order: [OrderParameters], $filter: [FilterParameters]) {
        ${fname} (params: {limit: $limit, offset: $offset, order: $order, filter: $filter}) {
          items { ${fieldsStr} }
        }
      }`;

      try {
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query,
            variables: fetchSettings,
          }),
          signal,
        });

        const result = await response.json();
        return result.data?.[fname]?.items ?? [];
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return [];
        }
        showToastError('GENERIC_ERROR');
        return [];
      }
    };

    const fetchData = async (): Promise<StringMap[]> => fetchDataWithSignal(settings.value, currentAbortController?.signal ?? new AbortController().signal);

    const filterOutSelected = (items: StringMap[]): StringMap[] => items.filter(
      (item) => !selectedList.value.some((selected) => selected[props.fields.id] === item[props.fields.id]),
    );

    const handleValueChange = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      // Annuler la requête précédente
      if (currentAbortController) {
        currentAbortController.abort();
      }

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = searchValue.value.trim();
      if (!searchTerm) {
        availableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        // Créer un nouveau AbortController pour cette recherche
        currentAbortController = new AbortController();
        const { signal } = currentAbortController;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        availableList.value = [];

        const isFulltextType = props.name in FULLTEXT_CONFIG;
        const isSingleWord = !searchTerm.includes(' ');

        settings.value.limit = PAGE_SIZE;
        settings.value.offset = 0;

        // Utiliser FULLTEXT (Against) pour products, categories et manufacturers
        // Le filtre de recherche DOIT être en premier pour optimiser la requête FULLTEXT
        const searchOperator = isFulltextType ? OperatorType.Against : OperatorType.Contains;
        settings.value.filter = [
          { field: props.fields.label, value: searchTerm, operator: searchOperator },
          ...(props.defaultFilters ?? []),
        ];

        let results: StringMap[] = [];

        // Si FULLTEXT type + single word : recherche par nom ET par ID en parallèle
        if (isFulltextType && isSingleWord) {
          const idField = FULLTEXT_CONFIG[props.name];

          // Requête par ID (filtre ID en premier)
          const idSettings: ListSettings = {
            limit: 1,
            offset: 0,
            order: [],
            filter: [
              { field: idField, value: searchTerm, operator: OperatorType.Equals },
              ...(props.defaultFilters ?? []),
            ],
          };

          const [nameResults, idResults] = await Promise.all([
            fetchDataWithSignal(settings.value, signal),
            fetchDataWithSignal(idSettings, signal),
          ]);

          if (currentRequestId !== searchRequestId) return;

          // Fusionner : ID en premier, puis résultats par nom (sans doublons)
          results = [...idResults];
          nameResults.forEach((item) => {
            if (!results.some((r) => r[props.fields.id] === item[props.fields.id])) {
              results.push(item);
            }
          });
        } else {
          results = await fetchDataWithSignal(settings.value, signal);
          if (currentRequestId !== searchRequestId) return;
        }

        availableList.value = filterOutSelected(results);
        hasMoreResults.value = results.length >= PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value || !currentAbortController) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      settings.value.offset = currentOffset.value;

      const results = await fetchDataWithSignal(settings.value, currentAbortController.signal);

      if (currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (results.length > 0) {
        const newItems = filterOutSelected(results);
        availableList.value = [...availableList.value, ...newItems];
        hasMoreResults.value = results.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const handleClose = () => {
      context.emit('on-close-dialog');
    };

    const selectItem = (element: StringMap) => {
      selectedList.value.unshift(element);
      availableList.value = availableList.value.filter((el: StringMap) => el[props.fields.id] !== element[props.fields.id]);
    };

    const removeItem = (element: StringMap) => {
      selectedList.value = selectedList.value.filter((el: StringMap) => el[props.fields.id] !== element[props.fields.id]);
    };

    const handleChooseItems = () => {
      context.emit('on-choose-items', selectedList.value);
      handleClose();
    };

    onMounted(async () => {
      if (props.items && props.items !== '') {
        settings.value.filter = [
          { field: props.fields.id, value: props.items, operator: OperatorType.In },
        ];
        settings.value.limit = 1000;
        selectedList.value = await fetchData();
        settings.value.limit = PAGE_SIZE;
      }
    });

    return {
      t,
      searchValue,
      availableList,
      selectedList,
      displayModal,
      availableListRef,
      selectItem,
      removeItem,
      handleClose,
      handleValueChange,
      handleChooseItems,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
